import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { RichTextEditorDialogComponent } from '../rich-text-editor-dialog/rich-text-editor-dialog.component';
import {
  RICH_TEXT_CONTENT_FONT_CSS,
  RICH_TEXT_TABLE_LAYOUT_CSS,
} from '../rich-text-editor-dialog/rich-text-editor.config';
import { TinyMceWarmupService } from '../../../services/common/tinymce-warmup.service';
import {
  extractBodyHtml,
  isEmptyHtml,
  normalizeFragment,
} from '../../../utils/rich-text-html.util';

/**
 * CSS injected into the preview iframe so it renders like the editor surface:
 * the SAME content font and table sizing as the editor's `content_style`, no
 * document margin, clipped (no inner scrollbar — the pane shows what fits; see
 * the field's "show visible area is sufficient" contract), and minimal table
 * borders so pasted tables stay readable.
 *
 * Exported so the spec can assert the editor/preview parity directly — the two
 * surfaces must inject the same shared constants or a field visibly reflows the
 * moment the dialog opens.
 */
export const RICH_TEXT_PREVIEW_IFRAME_CSS =
  // overflow:hidden suppresses the iframe's OWN scrollbar — scrolling happens on
  // the pane (which sizes this iframe to its content height). body height stays
  // auto = content height, so scrollHeight still measures correctly.
  'html,body{margin:0;padding:0;overflow:hidden}' +
  `body{${RICH_TEXT_CONTENT_FONT_CSS}line-height:1.4;word-wrap:break-word}` +
  // Clamp the leading/trailing block margins so the content sits flush against
  // the pane's own padding, no matter what the source HTML's first/last element
  // margins are. SSM/TX27-generated docs carry an inline `margin` on the first
  // <p>, which made the top gap vary between sheets (e.g. role 740000 vs 750000);
  // `!important` beats those inline styles so the preview's top/bottom spacing is
  // identical across every rich-text field.
  'body>*:first-child{margin-top:0 !important}' +
  'body>*:last-child{margin-bottom:0 !important}' +
  'table{border-collapse:collapse}' +
  // Unsized tables fill the width, matching the government MBRS Prep Tool's
  // viewer (SSM markup carries no width at all). Shared with the editor's
  // `content_style` — see RICH_TEXT_TABLE_LAYOUT_CSS for the full rationale and
  // why this must never become a TinyMCE table default.
  RICH_TEXT_TABLE_LAYOUT_CSS +
  'td,th{border:1px solid #e5e7eb;padding:0.25rem 0.5rem}';

/**
 * Drop-in rich-text form field — a one-line replacement for the plain
 * `<textarea pTextarea [formControlName]="qname">` used by Explanatory /
 * TextBlock fields.
 *
 * It is a {@link ControlValueAccessor}, so it binds with `[formControlName]`
 * exactly like the textarea it replaces, and the form-control value stays a
 * **string** (an HTML fragment). The component:
 *   - renders a **read-only preview** of the current value inside a **sandboxed
 *     iframe** (see {@link previewDoc}) so the editor's formatting — fonts,
 *     colours, headings, lists, tables — shows with full fidelity while scripts
 *     stay disabled (XSS-safe),
 *   - shows an **expand** affordance that opens the universal
 *     {@link RichTextEditorDialogComponent}, and
 *   - normalizes the editor output back to the control via
 *     `normalizeFragment` (empty content → `''`, which keeps `required`
 *     validation in parity across the mounted + pure verdict paths).
 *
 * Loading is symmetric: `extractBodyHtml` unwraps a legacy full XHTML document
 * (the SSM/N8N value shape) to its `<body>` for both preview and editing.
 *
 * Validation display: the host passes `[invalid]` (typically
 * `control.invalid && control.touched`) so the field shows the same red border
 * the textarea did. The field stays validation-agnostic otherwise.
 */
@Component({
  selector: 'app-rich-text-field',
  standalone: true,
  imports: [CommonModule, RichTextEditorDialogComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextFieldComponent),
      multi: true,
    },
  ],
  templateUrl: './rich-text-field.component.html',
  styleUrls: ['./rich-text-field.component.css'],
  // The field lives inside the heavy role-24 form (a large dual number-input
  // table) under default change detection, so it was checked on every app-wide
  // CD tick. OnPush limits it to input/event-driven checks; the CVA callbacks
  // (writeValue / setDisabledState) aren't events, so they call `markForCheck`.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextFieldComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  /** Heading shown in the pop-out (usually the field label). */
  @Input() title = 'Edit content';

  /** Shown in the preview area when there is no content. */
  @Input() placeholder = 'Click to add content…';

  /** Drives the red invalid border. Host passes `invalid && touched`. */
  @Input() invalid = false;

  /** Canonical form-control value (an HTML string; may be a legacy full doc). */
  value = '';

  /** Whether the editor pop-out is open. */
  dialogVisible = false;

  /**
   * Snapshot of the HTML handed to the editor, taken once at open time. Bound
   * to the dialog (not the live `value` getter) so a "Save" that keeps the
   * dialog open does not re-feed — and reset the cursor of — the active editor.
   */
  openingHtml = '';

  /** CVA disabled state. */
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private warmup = inject(TinyMceWarmupService);

  // ---- Lifecycle ------------------------------------------------------------

  /**
   * Warm the TinyMCE runtime in the background as soon as a rich-text field
   * mounts, so the first pop-out open is fast. Idempotent + lazy: it runs once
   * per session and only because the user reached a rich-text sheet. See
   * {@link TinyMceWarmupService}.
   */
  ngOnInit(): void {
    this.warmup.warm();
  }

  /**
   * Hover/focus on the preview is a strong "about to open" signal, so expedite
   * the warm-up (run it now instead of waiting for the browser-idle slot the
   * on-mount call uses). Idempotent — a no-op once warming has started. Skipped
   * when disabled, since a disabled field can't open the editor.
   */
  onPreviewIntent(): void {
    if (!this.disabled) this.warmup.warm(true);
  }

  // ---- ControlValueAccessor -------------------------------------------------

  writeValue(value: string | null): void {
    // Canonicalize on load to the SAME shape a `commit()` produces — the trimmed
    // body fragment (`normalizeFragment(extractBodyHtml(...))`). Legacy/backend
    // values arrive as full XHTML documents (`<?xml?><html>…<body>…</body></html>`),
    // but the editor only ever emits fragments. Storing the raw full doc in the
    // control while an edit persists a fragment left a permanent modified-vs-
    // original diff, so the sheet-selector pencil never cleared after the user
    // reverted the content (see FilingFormStateService.reconcileEditedFlag). Making
    // the loaded value match the editor's output lets an edit→revert round-trip.
    const incoming = value ?? '';
    const canonical = normalizeFragment(extractBodyHtml(incoming));
    // Skip redundant rebuilds: re-feeding a value that canonicalizes to the same
    // string would create a NEW SafeHtml (a new `[srcdoc]` reference), forcing the
    // preview iframe to reload + re-measure for nothing. Angular re-feeds the same
    // value in several flows (patchValue, status recalcs, the form-edit pipeline
    // round-trip), so this guard avoids a visible flicker + needless work.
    if (canonical === this.value) return;
    this.value = canonical;
    this.refreshPreview();
    this.cdr.markForCheck();
    // Propagate the canonical fragment back to the form model when normalization
    // actually changed the string (e.g. a legacy full doc, or trailing whitespace),
    // so the persisted value AND the revert baseline match the editor's output.
    // A value already in canonical form does not fire onChange — no spurious edit.
    if (canonical !== incoming) {
      this.onChange(canonical);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ---- View helpers ---------------------------------------------------------

  /**
   * Cached `srcdoc` for the preview iframe — a full HTML document recomputed
   * only when `value` changes (writeValue / commit), not per change-detection
   * tick, so the bound `[srcdoc]` reference is stable and the iframe doesn't
   * reload on every CD pass.
   */
  private _previewDoc: SafeHtml = '';

  /**
   * The `srcdoc` document for the preview iframe (see template). The iframe runs
   * with `sandbox="allow-same-origin"` — scripting stays disabled (no
   * `allow-scripts`, so no `<script>`, no inline handlers, no `javascript:`
   * URLs), so we render the editor's HTML with full fidelity (inline `style`
   * from TinyMCE: fonts, colours, sizes; native headings/lists/tables) WITHOUT
   * the XSS risk of injecting it into the live DOM. `allow-same-origin` (safe
   * without `allow-scripts`) is needed only so the host can read the document's
   * content height and size the iframe to it — see {@link syncFrameHeight}. The
   * document injects the SAME `RICH_TEXT_CONTENT_FONT_CSS` as the editor's
   * `content_style`, so the two render identically. We `bypassSecurityTrustHtml`
   * for the srcdoc string (Angular sanitizes the srcdoc attribute as HTML
   * otherwise) — safe because the **sandbox**, not the sanitizer, neutralises
   * scripts.
   */
  get previewDoc(): SafeHtml {
    return this._previewDoc;
  }

  private refreshPreview(): void {
    // Memoize emptiness here (the only place `value` changes) instead of in the
    // `isEmpty` getter it replaced — that getter was bound in the template's
    // `*ngIf`, so its ~7-pass emptiness regex ran over the full HTML on EVERY
    // change-detection tick of the heavy host form. Now it runs once per value.
    this.isEmpty = isEmptyHtml(this.value);
    const doc =
      '<!doctype html><html><head><meta charset="utf-8"><style>' +
      RICH_TEXT_PREVIEW_IFRAME_CSS +
      '</style></head><body>' +
      extractBodyHtml(this.value) +
      '</body></html>';
    this._previewDoc = this.sanitizer.bypassSecurityTrustHtml(doc);
  }

  // ---- Iframe auto-height (so the pane can scroll the content) ---------------

  private frameRef?: ElementRef<HTMLIFrameElement>;
  private frameResizeObserver?: ResizeObserver;
  private lastFrameWidth = 0;

  /**
   * Capture the preview iframe when it appears (it sits behind `*ngIf`) and
   * watch its width, so we re-measure the content height whenever the layout
   * reflows (e.g. the responsive width fix changes the pane width).
   */
  @ViewChild('frame')
  set frame(ref: ElementRef<HTMLIFrameElement> | undefined) {
    this.frameRef = ref;
    this.frameResizeObserver?.disconnect();
    const el = ref?.nativeElement;
    if (!el) return;
    this.lastFrameWidth = el.clientWidth;
    this.frameResizeObserver = new ResizeObserver(() => {
      const clientWidth = el.clientWidth;
      if (clientWidth !== this.lastFrameWidth) {
        this.lastFrameWidth = clientWidth;
        this.syncFrameHeight();
      }
    });
    this.frameResizeObserver.observe(el);
  }

  /**
   * Size the iframe to its content's height so the surrounding pane
   * (fixed-height, `overflow-y: auto`, resizable) shows a scrollbar and scrolls
   * the whole content — the iframe itself is `pointer-events: none`, so the
   * scroll happens on the pane (and click-to-open / resize keep working). Runs
   * on the iframe `(load)` and on width-driven reflow. Reads the same-origin
   * srcdoc document's height (no scripting needed in the iframe).
   */
  syncFrameHeight(): void {
    const el = this.frameRef?.nativeElement;
    const doc = el?.contentDocument;
    if (!el || !doc) return;
    // Collapse first, then measure: `documentElement.scrollHeight` is floored at
    // the iframe's own (viewport) height, so it can never report a value smaller
    // than the iframe currently is. Without this reset, short content left the
    // iframe stuck at the pane height — overflowing the pane's padding — so the
    // pane showed a permanent vertical scrollbar even when the content fit.
    // Reset → measure → set lets the iframe shrink to the content, so the pane
    // only scrolls when the content genuinely exceeds it. The two writes happen
    // synchronously (the read in between forces layout, not paint), so there is
    // no flicker.
    el.style.height = '0px';
    const height = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
    );
    el.style.height = height > 0 ? `${height}px` : '';
  }

  ngOnDestroy(): void {
    this.frameResizeObserver?.disconnect();
  }

  /**
   * True when there is no visible content → show the placeholder. Memoized:
   * recomputed only when `value` changes (in {@link refreshPreview}), not per
   * change-detection tick. Initialised `true` for the empty starting value.
   */
  isEmpty = true;

  // ---- Interaction ----------------------------------------------------------

  /**
   * Height of the preview pane captured on pointer-down, used to tell a resize
   * drag apart from a plain click (see {@link onPreviewClick}).
   */
  private resizeProbeHeight = 0;

  /** Record the pane height when a press starts, before any resize drag. */
  onPreviewPointerDown(scrollEl: HTMLElement): void {
    this.resizeProbeHeight = scrollEl.offsetHeight;
  }

  /**
   * Open the editor on a genuine click — but NOT when the click is the tail of a
   * resize drag. Dragging the bottom-right grip changes the pane's height, so if
   * it differs from the pointer-down snapshot we swallow the click instead.
   */
  onPreviewClick(scrollEl: HTMLElement): void {
    if (scrollEl.offsetHeight !== this.resizeProbeHeight) return;
    this.open();
  }

  open(): void {
    if (this.disabled) return;
    // Snapshot the editing HTML once (legacy full docs unwrapped to body).
    this.openingHtml = extractBodyHtml(this.value);
    this.dialogVisible = true;
  }

  /** Save (stay open) commits the edited HTML to the form control. */
  commit(editorHtml: string): void {
    this.value = normalizeFragment(editorHtml);
    this.refreshPreview();
    this.onChange(this.value);
    this.onTouched();
  }

  onCancel(): void {
    this.onTouched();
  }
}
